import React, { useEffect, useState } from "react";
import { AppBar, Toolbar, IconButton, Avatar, Fab, Box, Typography, useMediaQuery } from "@mui/material";
import { Capacitor } from "@capacitor/core";
import { Home as HomeIcon } from "../icons";
import { Search as SearchIcon } from "../icons";
import { Forum as ForumIcon } from "../icons";
import { QrCodeScanner as QrCodeScannerIcon } from "../icons";
import NotificationIcon, { subscribeToNotifications } from "./NotificationIcon";
import ChatNavButton from "./ChatNavButton";
import PublishSpeedDial from "./PublishSpeedDial";
import QrActionsSheet from "../components/QrActionsSheet";
import { useSubscriptionContext } from "./SubscriptionContext";
import { auth, db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { Link, useLocation } from "react-router-dom";
import { pageContentSx, DESKTOP_MEDIA_QUERY } from "../pageLayout";
import { barColors } from "../theme";
import { useDesktopSidebar } from "../desktopSidebarContext";

// El botón central va al escáner de QR en la app nativa y en mobile web (con
// suscripción activa abre un panel para elegir entre escanear o generar un QR
// de cobro, ver QrActionsSheet), y a Hilos (red social) solo en desktop web. isNativePlatform()
// no cambia en runtime, así que no hace falta que sea reactivo; el ancho de
// pantalla sí (rotar/resize), por eso ese chequeo va dentro del componente.
const isNative = Capacitor.isNativePlatform();

// Caras que va alternando el botón central, cada FAB_FACE_MS: la "N" sobre
// blanco y el destino del botón sobre azul (Hilos en desktop web, QR en
// mobile y en la app; ver showHilos).
const FAB_BLUE = "#030553";
const FAB_FACE_MS = 3000;
const FAB_FACES = [
  { key: "n" },
  { key: "hilos", Icon: ForumIcon, blue: true },
  { key: "qr", Icon: QrCodeScannerIcon, blue: true },
];

// Envuelve un ítem de navegación y le agrega un subrayado debajo cuando
// corresponde a la sección activa. Declarado fuera de BottomToolbar para que
// su identidad de componente no cambie en cada render (evitaría remontar el
// ícono/ChatNavButton en cada cambio de ruta).
const NavItem = ({ active, children }) => (
  <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      {children}
      <Box
        sx={{
          position: "absolute",
          bottom: 2,
          left: "50%",
          transform: "translateX(-50%)",
          width: 16,
          height: 2,
          borderRadius: 1,
          backgroundColor: barColors.icon,
          opacity: active ? 1 : 0,
        }}
      />
    </Box>
  </Box>
);

// Notificaciones sin leer + foto de perfil del usuario actual, en tiempo
// real. Se exporta para la sidebar de escritorio (components/DesktopSidebar.js),
// que muestra los mismos datos.
export const useNavUserState = () => {
  const [hasNewNotifications, setHasNewNotifications] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState(null);

  useEffect(() => {
    let unsubNotif = null;
    let unsubProfile = null;

    const unsubAuth = auth.onAuthStateChanged((user) => {
      // Limpiar listeners anteriores si el usuario cambia
      if (unsubNotif) { unsubNotif(); unsubNotif = null; }
      if (unsubProfile) { unsubProfile(); unsubProfile = null; }

      if (user) {
        // Suscribirse a notificaciones no leídas (auth ya está listo aquí)
        unsubNotif = subscribeToNotifications(setHasNewNotifications);

        // Foto de perfil: listener en tiempo real, no una lectura única, para
        // que se actualice sola si el doc todavía no existía al montar o si
        // el usuario cambia su foto desde otra pantalla.
        unsubProfile = onSnapshot(
          doc(db, 'users', user.uid),
          (snap) => setProfileImageUrl(snap.data()?.profileImageUrl || null),
          (error) => {
            console.error("Error fetching user data:", error);
            setProfileImageUrl(null);
          }
        );
      } else {
        setHasNewNotifications(false);
        setProfileImageUrl(null);
      }
    });

    return () => {
      unsubAuth();
      if (unsubNotif) unsubNotif();
      if (unsubProfile) unsubProfile();
    };
  }, []);

  return { hasNewNotifications, profileImageUrl };
};

const BottomToolbar = () => {
  const sidebarActive = useDesktopSidebar();
  // Con la sidebar de escritorio no se dibuja: toda la navegación vive ahí.
  // Va como componente aparte para no montar sus listeners de Firestore ni
  // el intervalo del FAB cuando no se muestra.
  if (sidebarActive) return null;
  return <BottomToolbarBar />;
};

const BottomToolbarBar = () => {
  const { hasNewNotifications, profileImageUrl } = useNavUserState();
  const location = useLocation();
  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY);
  const showHilos = !isNative && isDesktop;

  const { isSubscriptionActive: hasSubscription } = useSubscriptionContext();
  const [qrSheetOpen, setQrSheetOpen] = useState(false);
  const [fabStep, setFabStep] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setFabStep((step) => step + 1), FAB_FACE_MS);
    return () => clearInterval(id);
  }, []);
  const fabSequence = showHilos ? ["n", "hilos"] : ["n", "qr"];
  const fabFace = FAB_FACES.find((f) => f.key === fabSequence[fabStep % fabSequence.length]);
  const fabBlue = !!fabFace.blue;
  const fabBg = fabBlue ? FAB_BLUE : "white";
  const fabFg = fabBlue ? "white" : FAB_BLUE;

  // Todos los íconos usan el accent de marca (varía por tema), sin distinguir
  // la sección activa con otro color/opacidad: esa sección se marca con un
  // subrayado (ver NavItem) en vez de eso.
  const iconSx = { color: barColors.icon };
  const isActive = (path) => location.pathname === path;

  return (
    <AppBar position="fixed" elevation={0} sx={{ top: "auto", bottom: 0, backgroundColor: barColors.background }}>
      <Toolbar className="tour-bottom-toolbar" sx={{ minHeight: '52px !important', height: '52px' }}>
      <Box sx={{ ...pageContentSx, px: { xs: 0, md: 2 }, display: 'flex', alignItems: 'center' }}>
        <NavItem active={isActive("/home")}>
          <IconButton id="tour-home" component={Link} to="/home" sx={iconSx}>
            <HomeIcon />
          </IconButton>
        </NavItem>

        <NavItem active={isActive("/members")}>
          <IconButton id="tour-search" component={Link} to="/members" sx={iconSx}>
            <SearchIcon />
          </IconButton>
        </NavItem>

        <NavItem active={isActive("/chats")}>
          <ChatNavButton sx={iconSx} />
        </NavItem>

        {/* Fab central: Hilos en desktop web; en mobile y app, escáner de QR (o el panel de QR con suscripción); alterna con la N (ver FAB_FACES) */}
        <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
        <Box sx={{ position: "relative", width: 80, height: 80, mt: 0 }}>
          <Fab
            id="tour-qrscanner-btn"
            {...(showHilos || !hasSubscription
              ? { component: Link, to: showHilos ? "/hilos" : "/QRScanner" }
              : { onClick: () => setQrSheetOpen(true), "aria-label": "QR" })}
            sx={{
              position: "absolute",
              top: "-15px",
              left: "50%",
              transform: "translateX(-50%)",
              width: 70,
              height: 70,
              backgroundColor: fabBg,
              border: "2px solid rgba(0,0,0,0.1)",
              boxShadow: `0 5px 15px rgba(0,0,0,0.2), inset 0 -2px 10px rgba(0,0,0,0.1), inset 0 2px 10px rgba(255,255,255,0.8)`,
              overflow: "visible",
              transition: "all 0.3s ease-in-out, background-color 0.6s ease",
              animation: "spherePulse 3s ease-in-out infinite",
              zIndex: 2,
              '&:hover': {
                transform: 'translateX(-50%) scale(1.05)',
                backgroundColor: fabBg,
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: '50%',
                background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.1) 50%, transparent 70%)',
                animation: 'moveLight 5s ease-in-out infinite',
                pointerEvents: 'none',
              },
              '@keyframes spherePulse': {
                '0%, 100%': {
                  transform: 'translateX(-50%) translateY(0px)',
                  boxShadow: `0 5px 15px rgba(0,0,0,0.2), inset 0 -2px 10px rgba(0,0,0,0.1), inset 0 2px 10px rgba(255,255,255,0.8)`
                },
                '50%': {
                  transform: 'translateX(-50%) translateY(-6px)',
                  boxShadow: `0 15px 15px rgba(0,0,0,0.1), inset 0 -2px 10px rgba(0,0,0,0.1), inset 0 2px 10px rgba(255,255,255,0.8)`
                }
              },
              '@keyframes moveLight': {
                '0%, 100%': { backgroundPosition: '30% 30%' },
                '50%': { backgroundPosition: '70% 70%' }
              },
              '@keyframes floatN': {
                '0%, 100%': { transform: 'translateY(0px)' },
                '50%': { transform: 'translateY(-2px)' }
              }
            }}
          >
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                animation: 'floatN 3s ease-in-out infinite',
                zIndex: 2,
              }}
            >
              {/* Las 3 caras quedan montadas una encima de otra y se cruzan con
                  un fundido + giro, así el cambio se ve animado. */}
              {FAB_FACES.map(({ key, Icon }) => {
                const visible = key === fabFace.key;
                return (
                  <Box
                    key={key}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: fabFg,
                      opacity: visible ? 1 : 0,
                      transform: visible ? 'scale(1) rotateY(0deg)' : 'scale(0.6) rotateY(90deg)',
                      transition: 'opacity 0.5s ease, transform 0.6s ease, color 0.6s ease',
                    }}
                  >
                    {Icon ? (
                      <Icon sx={{ fontSize: 34 }} />
                    ) : (
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 'bold',
                          color: 'inherit',
                          textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                          fontFamily: 'Arial, sans-serif',
                          userSelect: 'none',
                        }}
                      >
                        N
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Fab>
        </Box>
        </Box>

        <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
          <PublishSpeedDial />
        </Box>

        <NavItem active={isActive("/notifications")}>
          <IconButton id="tour-notifications" component={Link} to="/notifications" sx={iconSx}>
            <NotificationIcon hasNewNotifications={hasNewNotifications} />
          </IconButton>
        </NavItem>

        <NavItem active={isActive("/profile")}>
          <IconButton id="tour-profile" component={Link} to="/profile" sx={iconSx}>
            {profileImageUrl ? (
              <Avatar src={profileImageUrl} sx={{ width: 24, height: 24, border: `2.3px solid ${barColors.icon}` }} />
            ) : (
              <Avatar sx={{ width: 24, height: 24, border: `2.3px solid ${barColors.icon}` }} />
            )}
          </IconButton>
        </NavItem>
      </Box>
      </Toolbar>
      {hasSubscription && <QrActionsSheet open={qrSheetOpen} onClose={() => setQrSheetOpen(false)} />}
    </AppBar>
  );
};

export default BottomToolbar;
